import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { getDefaultConfigurations } from '../utils/Configs';
import { Logger } from '../utils/Logger';
import { ScreenTypes } from '../utils/ScreensList';
import { InformationContext, InformationType } from '../utils/contexts/information.context';
import { StyleContext } from '../utils/contexts/style.context';

import { CollectScreen } from '../templates/screens/CollectScreen';
import { HomeScreen } from '../templates/screens/HomeScreen';
import { GameScreen } from '../templates/screens/GameScreen';
import { ErrorScreen } from '../templates/screens/ErrorScreen';
import { WonScreen } from '../templates/screens/WonScreen';
import { LostScreen } from '../templates/screens/LostScreen';
import { NextScreen } from '../templates/screens/NextScreen';
import { AlreadyPlayedScreen } from '../templates/screens/AlreadyPlayedScreen';
import { SingleGameScreen } from '../templates/screens/SingleGameScreen';
import { MultiGameScreen } from '../templates/screens/MultiGameScreen';
import { Loader } from '../templates/common-components/Loader';

import { CommonStyle } from '../common-types/CommonDataConfig.type';
import { useAppDispatch, useAppSelector } from '../hooks';

import { CollectFormValues } from '../common-types/CollectForm.type';

import {
  displayErrorScreen,
  transitionAfterCollectScreen,
  transitionAfterWelcomeScreen,
  transitionFromGameHomeToGameScreen, transitionFromGameResultToNextGame, transitionFromGameToGameResult,
  transitionFromLoadingToFirstScreen,
} from '../slices/DisplayedScreen';
import { setCurrentGameConfiguration, setGameResult } from '../slices/GameExperience';
import { setInitialExperienceData } from '../slices/GlobalExperience';

import { experienceServiceInstance } from '../utils/services/ExperienceService';
import { collectServiceInstance } from '../utils/services/CollectService';
import { apiServiceInstance } from '../utils/services/ApiService';
import { eventServiceInstance } from '../utils/services/EventService';
import { personalDataServiceInstance } from '../utils/services/PersonalDataService';
import { setRetailerPersonalData } from '../slices/GlobalRetailerPersonalData';

export const ExperienceController: React.FunctionComponent = () => {
  const [informationContext, setInformationContext] = useState<InformationType>({});
  const [styleContext, setStyleContext] = useState<CommonStyle | null>(null);
  const [searchParams] = useSearchParams();

  const {
    siteExperienceConfiguration,
    thumbnails,
    games,
  } = useAppSelector((state) => state.globalExperience);
  const {
    hasWonCurrentGame,
    currentCampaign,
    currentGameConfiguration,
    currentTicket,
    currentIndex,
  } = useAppSelector((state) => state.gameExperience);
  const {
    displayedScreen,
    displayError,
    isLoading,
  } = useAppSelector((state) => state.displayScreen);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const ticketCode: string | null = searchParams.get('ticketCode');
    const siteKey = searchParams.get('siteKey');
    const customerUid = searchParams.get('customerUid');
    const cartUid = searchParams.get('cartUid');
    if (siteKey === null || customerUid === null || cartUid === null) {
      Logger.log('Missing data to display the experience');
      dispatch(displayErrorScreen());
      return;
    }
    Promise.all([
      personalDataServiceInstance.getCustomerPersonalData(siteKey, customerUid),
      experienceServiceInstance.getSiteInformations(siteKey),
      experienceServiceInstance.getGamesInformations(cartUid, customerUid, siteKey, ticketCode),
    ])
      .then(([personalData, siteInformation, gamesInformations]) => {
        if (gamesInformations.firstGameData === undefined) {
          Logger.log('No game found');
          dispatch(displayErrorScreen());
          return;
        }
        dispatch(setRetailerPersonalData({ retailerPersonalData: personalData }));
        dispatch(setInitialExperienceData({
          games: gamesInformations.games,
          siteConfiguration: siteInformation.config.screens,
        }));
        dispatch(setCurrentGameConfiguration({
          data: gamesInformations.firstGameData,
          index: 0,
        }));
        dispatch(transitionFromLoadingToFirstScreen({
          firstGameHasBeenPlayed: !gamesInformations.firstGameData.isGamePlayable,
          hasOnlyOneGame: gamesInformations.games.length === 1,
          siteConfiguration: siteInformation.config.screens,
        }));
        setInformationContext((prevState) => ({
          ...prevState,
          gameCount: gamesInformations.games.length,
        }));
        setStyleContext(siteInformation.config.commons.style);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentTicket !== null && currentCampaign !== null) {
      setInformationContext((previous: InformationType) => ({
        ...previous,
        campaign: currentCampaign,
        ticket: currentTicket,
      }));
    }
  }, [currentTicket, currentCampaign]);

  if (displayError || displayedScreen === ScreenTypes.ERROR) {
    return <ErrorScreen config={getDefaultConfigurations().error}/>;
  }

  if (isLoading) {
    return <Loader/>;
  }

  return <InformationContext.Provider value={{
    information: informationContext,
    setInformation: setInformationContext,
  }}>
    <StyleContext.Provider value={{
      setStyle: setStyleContext,
      style: styleContext,
    }}>
      {
        displayedScreen === ScreenTypes.SINGLEGAME && siteExperienceConfiguration !== null &&
        <SingleGameScreen
          ctaOnClick={(): void => {
            dispatch(transitionAfterWelcomeScreen({ collectScreenIsActivated: siteExperienceConfiguration?.collect.activated }));
          }}
          config={siteExperienceConfiguration.singleGame}
          thumbnailUrls={thumbnails}
        />
      }
      {
        displayedScreen === ScreenTypes.MULTIGAME && siteExperienceConfiguration !== null &&
        <MultiGameScreen
          ctaOnClick={(): void => {
            dispatch(transitionAfterWelcomeScreen({ collectScreenIsActivated: siteExperienceConfiguration?.collect.activated }));
          }}
          config={siteExperienceConfiguration.multiGame}
          thumbnailUrls={thumbnails}
        />
      }
      {
        displayedScreen === ScreenTypes.COLLECT && siteExperienceConfiguration !== null &&
        <CollectScreen
          config={siteExperienceConfiguration.collect}
          onSubmit={(values: CollectFormValues): void => {
            if (currentTicket !== null) {
              const customerUid = searchParams.get('customerUid') as string;
              const siteKey = searchParams.get('siteKey') as string;
              Promise.all([
                collectServiceInstance.sendConsents(currentTicket.code, values.consents, siteKey, customerUid),
                collectServiceInstance.sendPersonalData(currentTicket.code, values.personalData),
              ])
                .then(() => {
                  dispatch(transitionAfterCollectScreen());
                })
                .catch((err) => {
                  Logger.log(err);
                  dispatch(displayErrorScreen());
                });
            }
          }}
        />
      }
      {
        currentGameConfiguration !== null && displayedScreen === ScreenTypes.HOME &&
        <HomeScreen
          config={currentGameConfiguration.home}
          ctaOnClick={(): void => {
            dispatch(transitionFromGameHomeToGameScreen());
          }}
        />
      }
      {
        currentGameConfiguration !== null && displayedScreen === ScreenTypes.GAME &&
        <GameScreen
          config={currentGameConfiguration.game}
          hasWon={hasWonCurrentGame}
          onGameBegin={(): void => {
            const customerUid = searchParams.get('customerUid');
            const siteKey = searchParams.get('siteKey');
            const cartUid = searchParams.get('cartUid');
            if (currentTicket === null || customerUid === null || siteKey === null || cartUid === null) {
              Logger.log('Missing experience data to display the experience');
              dispatch(displayErrorScreen());
            } else {
              apiServiceInstance.fetchGameResult(currentTicket.code, customerUid, siteKey, cartUid)
                .then((response) => {
                  dispatch(setGameResult(response.play.win));
                  setInformationContext((previous: InformationType) => {
                    const previousTicket = previous.ticket || {};
                    return {
                      ...previous,
                      ticket: {
                        ...previousTicket,
                        ...response,
                      },
                    } as unknown as InformationType;
                  });
                  // @TODO remove this event
                  eventServiceInstance.sendGamePlayedEvent(response.play.win, {
                    shopperUid: customerUid === null
                      ? 'undefined'
                      : customerUid,
                    siteKey: siteKey === null
                      ? 'undefined'
                      : siteKey,
                    ticket: currentTicket,
                    cartUid,
                  });
                })
                .catch((err) => {
                  Logger.log(err);
                  dispatch(displayErrorScreen());
                });
            }
          }}
          onGameDone={(): void => {
            if (hasWonCurrentGame === null) {
              Logger.log('No information to display result screen');
              dispatch(displayErrorScreen());
              return;
            }
            dispatch(transitionFromGameToGameResult({ hasWon: hasWonCurrentGame }));
          }}
        />
      }
      {
        currentGameConfiguration !== null && displayedScreen === ScreenTypes.WON &&
        <WonScreen config={currentGameConfiguration.won}/>
      }
      {
        currentGameConfiguration !== null && displayedScreen === ScreenTypes.LOST &&
        <LostScreen config={currentGameConfiguration.lost}/>
      }
      {
        currentGameConfiguration !== null && displayedScreen === ScreenTypes.ALREADY_PLAYED &&
        <AlreadyPlayedScreen config={currentGameConfiguration.alreadyPlayed}/>
      }
      {
        currentGameConfiguration !== null &&
        (displayedScreen === ScreenTypes.ALREADY_PLAYED || displayedScreen === ScreenTypes.WON || displayedScreen === ScreenTypes.LOST) &&
        games[currentIndex + 1] !== undefined &&
        <NextScreen
          config={games[currentIndex + 1].gameConfiguration.next}
          ctaOnClick={(): void => {
            const newIndex = currentIndex + 1;
            dispatch(setCurrentGameConfiguration({
              data: games[newIndex],
              index: newIndex,
            }));
            dispatch(transitionFromGameResultToNextGame());
          }}
        />
      }
    </StyleContext.Provider>
  </InformationContext.Provider>;
};
